import React, { useState, useEffect } from 'react';
import './QuickLeaguesPage.css';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { LEAGUE_TYPE, QL_ARRAY, TRAD_ARRAY } from '../constants/leagueTypes';

interface TeamFromDB {
  id: string; name: string; city: string; abbreviation: string;
  league_id: string; league_name?: string;
  wins: number; losses: number;
  primary_color?: string;
}

const QLMyTeamsPage: React.FC = () => {
  const [teams, setTeams] = useState<TeamFromDB[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTeams();
  }, []);

  async function loadTeams() {
    setLoading(true);
    if (!isSupabaseConfigured() || !supabase) { setLoading(false); return; }

    const userId = localStorage.getItem('userId');
    if (!userId) { setTeams([]); setLoading(false); return; }

    // Get QL league IDs
    const { data: qlLeagues } = await supabase
      .from('leagues')
      .select('id, name')
      .in('league_type', QL_ARRAY as readonly string[]);

    const qlLeagueIds = (qlLeagues || []).map(l => l.id);
    const leagueNames: Record<string, string> = {};
    (qlLeagues || []).forEach(l => { leagueNames[l.id] = l.name; });

    if (qlLeagueIds.length === 0) {
      setTeams([]);
      setLoading(false);
      return;
    }

    // Get user's teams in QL leagues
    const { data: owned } = await supabase
      .from('teams')
      .select('id, name, city, abbreviation, league_id, wins, losses')
      .eq('owner_id', userId)
      .in('league_id', qlLeagueIds);

    const enriched = (owned || []).map(t => ({
      ...t,
      league_name: leagueNames[t.league_id] || 'Unknown League',
    }));

    setTeams(enriched);
    setLoading(false);
  }

  if (loading) return <div className="quick-leagues-page"><p>Loading your teams...</p></div>;

  return (
    <div className="quick-leagues-page">
      <h1>⚡ My QL Teams</h1>
      <p className="subtitle">Your Quick League teams</p>

      {teams.length === 0 ? (
        <div className="no-leagues">
          <p>You don't have any Quick League teams yet.</p>
          <p style={{ marginTop: 12 }}>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: '/quick-league-lobby' }))}
              style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 6, cursor: 'pointer', fontSize: '1rem' }}
            >
              Join a Quick League →
            </button>
          </p>
        </div>
      ) : (
        <div className="ql-grid">
          {teams.map(team => (
            <div
              key={team.id}
              className="ql-card"
              onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: `/ql-team/${team.league_id}/${team.id}` }))}
            >
              <div
                className="ql-card-header"
                style={{ background: '#3b82f6' }}
              >
                <span className="ql-name" style={{ color: '#fff' }}>{team.city} {team.name}</span>
                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>{team.abbreviation}</span>
              </div>
              <div className="ql-card-body">
                <div className="ql-info-row">
                  <span>🏆 League</span>
                  <span>{team.league_name}</span>
                </div>
                <div className="ql-info-row">
                  <span>📊 Record</span>
                  <span>{team.wins}-{team.losses}</span>
                </div>
              </div>
              <div className="ql-card-footer">
                <span className="ql-view">Manage Team →</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QLMyTeamsPage;
